import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import pinPrompt from "../utils/pinPrompt";
import { API_BASE, apiJson, loadFormCatalog, putToS3WithProgress, fmtBytes } from "../utils/taxCertificates";

/**
 * Office side: record a certificate that arrived by email / on paper, or
 * attach the file to an open request (certificateId). Uploads the PDF/photo
 * browser → S3 (presigned PUT into the private jobq-customer-documents
 * bucket), then POSTs the record. The file is optional so a paper copy in the
 * filing cabinet can still be recorded (a note saying where it is required
 * to verify one without a file).
 */
export default function TaxCertRecordModal({ customer, certificateId = null, initial = {}, onClose, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [file, setFile] = useState(null);
  const [formType, setFormType] = useState(initial.form_type && initial.form_type !== "UNKNOWN" ? initial.form_type : "ST-28D");
  // Opened from "Edit" on a received row → stay 'received'; Verify is its own button.
  const [status, setStatus] = useState(initial.status === "received" ? "received" : "verified");
  const [signedOn, setSignedOn] = useState(initial.signed_on || "");
  const [expiresOn, setExpiresOn] = useState(initial.expires_on || "");
  const [taxId, setTaxId] = useState(initial.tax_id || "");
  const [taxIdState, setTaxIdState] = useState(initial.tax_id_state || "KS");
  const [signerName, setSignerName] = useState(initial.signer_name || "");
  const [purchaserName, setPurchaserName] = useState(initial.purchaser_name || customer?.name || "");
  const [notes, setNotes] = useState(initial.notes || "");
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { loadFormCatalog().then(setCatalog).catch(() => setCatalog(null)); }, []);
  const forms = (catalog?.forms || []).filter((f) => f.code !== "UNKNOWN");

  async function save() {
    setErr(null);
    if (!file && !initial.has_file && status === "verified" && !notes.trim()) {
      setErr("No file attached — add a note saying where the signed paper copy is, or set the status to 'Received' for now.");
      return;
    }
    const pin = await pinPrompt("Enter your PIN to record this certificate:");
    if (!pin) return;
    setBusy(true);
    try {
      let s3Key = null;
      let contentType = null;
      if (file) {
        contentType = file.type || "application/octet-stream";
        const u = await apiJson(`${API_BASE}/customers/${customer.customer_id}/tax_certificates/upload-url`, {
          method: "POST",
          body: JSON.stringify({ pin, file_name: file.name, content_type: contentType, file_size: file.size }),
        });
        setProgress(0);
        await putToS3WithProgress(u.upload_url, file, u.content_type, setProgress);
        s3Key = u.s3_key;
        contentType = u.content_type;
      }
      const body = {
        pin,
        certificate_id: certificateId || null,
        form_type: formType,
        status,
        s3_key: s3Key,
        file_name: file ? file.name : null,
        content_type: contentType,
        purchaser_name: purchaserName.trim() || null,
        tax_id: taxId.trim() || null,
        tax_id_state: taxIdState.trim() || null,
        signer_name: signerName.trim() || null,
        signed_on: signedOn || null,
        expires_on: expiresOn || null,
        notes: notes.trim() || null,
      };
      const r = await apiJson(`${API_BASE}/customers/${customer.customer_id}/tax_certificates`, {
        method: "POST", body: JSON.stringify(body),
      });
      onSaved && onSaved(r);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  return (
    <Modal show onHide={busy ? undefined : onClose} size="lg" backdrop="static" centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>{certificateId ? `Attach certificate to request #${certificateId}` : "Record a received certificate"} — {customer?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-7">
            <label className="form-label">Signed certificate (PDF or photo)</label>
            <input type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,application/pdf,image/*"
                   onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={busy} />
            <div className="form-text">
              {file ? <>{file.name} · {fmtBytes(file.size)}</> : (initial.has_file ? "Leave empty to keep the file already on this request." : "Optional — leave empty for a paper-only record (say where it is in the notes).")}
            </div>
            {progress !== null && (
              <div className="progress mt-2" style={{ height: 8 }}>
                <div className="progress-bar" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
          <div className="col-md-5">
            <label className="form-label">Form</label>
            <select className="form-select" value={formType} onChange={(e) => setFormType(e.target.value)}>
              {forms.length === 0 && <option value={formType}>{formType}</option>}
              {forms.map((f) => <option key={f.code} value={f.code}>{f.code} — {f.short || f.title}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Date signed</label>
            <input type="date" className="form-control" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Expires (optional)</label>
            <input type="date" className="form-control" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
            <div className="form-text">Kansas blanket certificates don't expire while the relationship is ongoing; KDOR-issued PR-78 ones do.</div>
          </div>
          <div className="col-md-4">
            <label className="form-label">Record as</label>
            <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="verified">Verified — checked, on file</option>
              <option value="received">Received — still needs review</option>
            </select>
          </div>
          <div className="col-md-5">
            <label className="form-label">Tax registration / exemption number on the form</label>
            <input type="text" className="form-control" value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="Kansas sales tax # or exemption #" />
          </div>
          <div className="col-md-2">
            <label className="form-label">State</label>
            <input type="text" className="form-control" maxLength={2} value={taxIdState} onChange={(e) => setTaxIdState(e.target.value.toUpperCase())} />
          </div>
          <div className="col-md-5">
            <label className="form-label">Signed by</label>
            <input type="text" className="form-control" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Purchaser name on the form</label>
            <input type="text" className="form-control" value={purchaserName} onChange={(e) => setPurchaserName(e.target.value)} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Notes</label>
            <input type="text" className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. paper copy in the accounting binder" />
          </div>
          {err && <div className="col-12"><div className="alert alert-danger mb-0">{err}</div></div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? (progress !== null ? `Uploading ${progress}%…` : "Saving…") : "Save certificate"}</button>
      </Modal.Footer>
    </Modal>
  );
}
